<template>
  <div id="dashboard-page">
    <!-- Company selector -->
    <div class="toolbar" v-show="companies.length > 1">
      <label for="company-select">{{ $t('dashboard.company') }}:</label>
      <select v-model="companyId" class="select-s2 browser-default small-contents">
        <option v-for="company in companies" :key="company.Item1" :value="company.Item1">
          {{ company.Item2 }}
        </option>
      </select>
    </div>

    <!-- Overview -->
    <dashboard-overview
      :companyId="companyId"
      :updated="assetViewUpdated">
    </dashboard-overview>

    <div class="tab-content">

      <!-- Tabs -->
      <div class="tab-bar">
        <div
          @click="switchTab('asset')"
          class="tab clickable"
          :class="{'active': tab === 'asset'}"
        >
          {{ $t('dashboard.assetview')}}
        </div>
        <div
          v-if="hasFeature(1)"
          @click="switchTab('feature')"
          class="tab clickable"
          :class="{'active': tab === 'feature'}"
        >
          {{$t('dashboard.feature1')}}
        </div>
      </div>

      <!-- Tab content -->
      <div class="tab-page">
        <!-- Asset View -->
        <keep-alive>
          <dashboard-asset-view
            v-if="tab === 'asset'"
            :companyId="companyId"
            @asset-selected="onAssetSelected"
            @updating="onUpdating"
          ></dashboard-asset-view>
        </keep-alive>

        <!-- Feature KVB -->
        <dashboard-feature-kvb
          v-if="tab === 'feature' && hasFeature(1)"
          :companyId="companyId"
          :parentId="parentId"
        ></dashboard-feature-kvb>
      </div>
    </div>
  </div>
</template>

<script>
import AuthenticationService from '@/services/AuthenticationService'
import DashboardService from '@/services/DashboardService'
import RailObjectService from '@/services/RailObjectService'
import Overview from './_Overview.vue'
import AssetView from './_AssetView.vue'
import FeatureKVB from './_FeatureKVB.vue'

export default {
  name: 'dashboard',
  components: {
    'dashboard-overview': Overview,
    'dashboard-asset-view': AssetView,
    'dashboard-feature-kvb': FeatureKVB
  },
  data () {
    return {
      currentUser: AuthenticationService.getUser(),
      tab: 'asset', // Default tab
      railObjects: [],
      companies: [],
      lastUpdated: new Date(),
      assetViewUpdated: null,
      companyId: null,
      parentId: null,
      features: []
    }
  },
  async created () {
    try {
      const response = await DashboardService.getCompanies(this.currentUser.UserId)
      this.companies = response.data

      // Add a "None" option
      this.companies.unshift({ Item1: null, Item2: 'None' })

      // Retrieve the previously selected company from localStorage
      const storedCompanyId = Number(localStorage.getItem('selectedCompanyId'))

      // Check if storedCompanyId exists in the companies list
      const companyExists = this.companies.some(company => company.Item1 === storedCompanyId)

      // Select the companyId: either stored value (if valid) or user's companyId (if in list) or default to null
      this.companyId = companyExists
        ? storedCompanyId
        : (this.companies.some(company => company.Item1 === this.currentUser.CompanyId)
          ? this.currentUser.CompanyId
          : null)

      // Get features to determine which tabs should be shown
      await this.fetchFeatures()

      this.railObjects = RailObjectService.getRailObjects()
      this.lastUpdated = new Date()
    } catch (error) {
    }
  },
  watch: {
    companyId (newVal) {
      localStorage.setItem('selectedCompanyId', newVal)
      this.fetchFeatures()
    }
  },
  methods: {
    switchTab (newTab) {
      // Only switch if the tab is valid based on features
      if (newTab === 'feature' && !this.hasFeature(1)) {
        return
      }
      this.tab = newTab
    },

    onAssetSelected (parentId) {
      this.parentId = parentId
      // Switch to Feature KVB tab only if feature 1 is available
      if (this.hasFeature(1)) {
        this.switchTab('feature')
      }
    },

    onUpdating () {
      this.assetViewUpdated = Date.now()
    },

    hasFeature (featureId) {
      return this.features.includes(featureId)
    },

    async fetchFeatures () {
      if (this.companyId === null) {
        this.features = []
        return
      }

      try {
        const featuresResponse = await DashboardService.getFeatures(this.currentUser.UserId, this.companyId)
        this.features = featuresResponse.data || []

        // Switch to valid tab if invalid feature is active
        if (this.tab === 'feature' && !this.hasFeature(1)) {
          this.switchTab('asset')
        }
      } catch (error) {
        this.features = []
      }
    }
  }
}
</script>

<style scoped>
#dashboard-page {
  width: 100%;
  height: calc(100vh - 56px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Toolbar styles */
.toolbar {
  width: 100%;
  background-color: white;
  padding: 5px 8px;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #ccc;
  height: 35px;
  font-size: 12px;
  flex-shrink: 0;
}

.toolbar label {
  margin-right: 5px;
  font-size: 12px;
}

.toolbar select {
  padding: 2px 4px;
  border: 1px solid #ccc;
  border-radius: 3px;
  font-size: 12px;
  height: 24px;
  width: auto;
  min-width: 50px;
  max-width: 100%;
}

/* Tab styles */
.tab-content {
  width: 100%;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.tab-bar {
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 40px;
  background-color: #f4f4f4;
  border-bottom: 1px solid #ccc;
  position: relative;
  flex-shrink: 0;
}

.tab {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px 0;
  color: #56CCF2;
  cursor: pointer;
  position: relative;
  transition: color 0.3s;
}

.tab.active {
  color: #56CCF2;
  font-weight: bold;
}

.tab.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  width: 100%;
  height: 3px;
  background-color: #56CCF2;
}

.tab-page {
  width: 100%;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

:deep(.asset-container),
:deep(.feature-container) {
  height: 100% !important;
  flex-grow: 1;
}

.clickable {
  pointer-events: auto !important;
}
</style>



// WEBPACK FOOTER //
// src/pages/dashboard3/Dashboard.vue